import React from 'react';
import { Button, Card, Table, Alert, Spin, Tooltip } from '@bancobarigui/barihaus';
import { DeleteOutlined, CopyOutlined, EditOutlined } from '@ant-design/icons';
import { Switch } from 'antd';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { UserPermissions } from '@/shared/enums/UserPermissions';

interface Checklist {
  id: string;
  name: string;
  inspectionTypeName?: string;
  version: number;
  isActive: boolean;
  createdAt: string;
  deactivatedAt?: string;
}

interface ChecklistsTableSectionProps {
  columns: any[];
  checklists: Checklist[];
  totalRecords: number;
  currentPage: number;
  pageSize: number;
  loading?: boolean;
  error?: string | null;
  handleEdit: (id: string) => void;
  handleDelete: (id: string) => void;
  handleCopy: (id: string) => void;
  handleActivate: (id: string) => void;
  handleDeactivate: (id: string) => void;
  handlePageChange: (page: number, size?: number) => void;
}

export function ChecklistsTableSection({
  columns,
  checklists,
  totalRecords,
  currentPage,
  pageSize,
  loading = false,
  error = null,
  handleEdit,
  handleDelete,
  handleCopy,
  handleActivate,
  handleDeactivate,
  handlePageChange,
}: ChecklistsTableSectionProps) {
  const { hasPermission } = usePermissions();
  const tableColumns = React.useMemo(() => [
    ...columns,
    {
      title: '',
      key: 'actions',
      width: 180,
      render: (_: unknown, record: Checklist) => {
        const canUpdate = hasPermission([UserPermissions.checklist_update_bank]);
        const canDelete = hasPermission([UserPermissions.checklist_delete_bank]);
        
        return (
          <div className="flex items-center gap-1">
            {canUpdate && (
              <Tooltip title="Editar">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record.id)}
                  className="flex items-center justify-center"
                  style={{marginRight: -24}}
                  children={undefined}
                />
              </Tooltip>
            )}
            
            {/* {canUpdate && (
              <Tooltip title="Baixar PDF">
                <Button
                  type="text"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopy(record.id)}
                  className="flex items-center justify-center"
                  children={undefined}
                />
              </Tooltip>
            )} */}
            
            {canUpdate && (
              <Tooltip title={record.isActive ? 'Desativar' : 'Ativar'}>
                <div className="flex items-center justify-center" style={{ width: '32px', height: '32px' }}>
                  <Switch
                    checked={record.isActive}
                    onChange={(checked) => checked ? handleActivate(record.id) : handleDeactivate(record.id)}
                    size="small"
                    style={{ backgroundColor: record.isActive ? '#1890ff' : undefined }}
                  />
                </div>
              </Tooltip>
            )}
            
            {canDelete && (
              <Tooltip title="Deletar">
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(record.id)}
                  danger
                  className="flex items-center justify-center"
                  children={undefined}
                />
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ], [columns, handleEdit, handleDelete, handleCopy, handleActivate, handleDeactivate]);

  return (
    <Card>
      {error && (
        <Alert
          message="Erro ao carregar checklists"
          description={error}
          type="error"
          showIcon
          closable
          className="mb-4"
        />
      )}

      <Spin spinning={loading}>
        <Table
          bordered
          columns={tableColumns}
          dataSource={checklists}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalRecords,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} de ${total} checklists`,
            onChange: handlePageChange,
          }}
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: 'Nenhum checklist encontrado',
          }}
        />
      </Spin>
    </Card>
  );
}
